/* eslint-disable react/prop-types */
import { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, FormControl, InputLabel,
  Select, MenuItem, Typography, Box, IconButton, Switch,
} from "@mui/material";
import {
  Close as CloseIcon, Edit as EditIcon,
  Business as BusinessIcon, Engineering as EngineeringIcon,
  FireExtinguisher, SettingsInputComponent as TechIcon,
  ErrorOutline as CriticalIcon,
} from "@mui/icons-material";
import { usePlanta } from "../../../context/PlantaContext";
import { UrlApi } from "../../../../../services/apirest";
import axios from "axios";
import moment from "moment";

function SectionHeader({ icon, label }) {
  return (
    <Box sx={{
      display: "flex", alignItems: "center", gap: 1,
      px: 1.5, py: 0.9,
      bgcolor: "#f8fafc",
      borderLeft: "3px solid #3b82f6",
      borderRadius: "0 6px 6px 0",
    }}>
      <Box sx={{ color: "#3b82f6", display: "flex", alignItems: "center" }}>{icon}</Box>
      <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", color: "#94a3b8", textTransform: "uppercase" }}>
        {label}
      </Typography>
    </Box>
  );
}

export function EditEquipmentForm({ equipo, open, onClose, refetch, showToast }) {
  const [formData, setFormData] = useState({ ...equipo });
  const { plantas } = usePlanta();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    await axios.put(UrlApi + "/mp_equipos/" + equipo.id, {
      ...formData,
      fecha_carga: formData.fecha_carga ? moment(formData.fecha_carga).format("YYYY-MM-DD") : null,
      fechavto: formData.fechavto ? moment(formData.fechavto).format("YYYY-MM-DD") : null,
    })
      .then(() => showToast("Equipo editado correctamente", "success"))
      .catch(() => showToast("Error al editar el equipo", "error"));
    await refetch();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ p: 0, borderBottom: "1px solid #e2e8f0" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2.5, py: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{
              width: 34, height: 34, borderRadius: 1.5,
              bgcolor: "#eff6ff", border: "1px solid #bfdbfe",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <EditIcon sx={{ fontSize: 17, color: "#3b82f6" }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#1e293b", lineHeight: 1.2 }}>
                Editar Equipo
              </Typography>
              <Typography sx={{ color: "#94a3b8", fontSize: 11, mt: 0.2 }}>
                #{equipo?.id} · {equipo?.equipo}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 2.5 }}>
          <Grid container spacing={2}>

            <Grid item xs={12}>
              <SectionHeader icon={<BusinessIcon sx={{ fontSize: 14 }} />} label="Información Básica" />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth required size="small">
                <InputLabel>Planta</InputLabel>
                <Select name="id_planta" value={formData.id_planta} onChange={handleChange} label="Planta">
                  {plantas?.map((item) => (
                    <MenuItem key={item.id_planta} value={item.id_planta}>{item.sucursal}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Nombre del Equipo" name="equipo"
                value={formData.equipo ?? ""} onChange={handleChange} required />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="ID del Equipo Original" name="id_origen"
                value={formData.id_origen ?? ""} onChange={handleChange} />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth required size="small">
                <InputLabel>Estado</InputLabel>
                <Select name="estado" value={formData.estado ?? ""} onChange={handleChange} label="Estado">
                  <MenuItem value="en servicio">En Servicio</MenuItem>
                  <MenuItem value="Fuera de Servicio">Fuera de Servicio</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth required size="small">
                <InputLabel>Sección</InputLabel>
                <Select name="sector" value={formData.sector ?? ""} onChange={handleChange} label="Sección">
                  {[1, 2, 3, 4].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Zona" name="zona"
                value={formData.zona ?? ""} onChange={handleChange} />
            </Grid>

            {/* Matafuegos toggle */}
            <Grid item xs={12}>
              <Box sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                px: 2, py: 1.25, borderRadius: 1.5,
                bgcolor: formData.es_matafuegos ? "#fef2f2" : "#f8fafc",
                border: `1px solid ${formData.es_matafuegos ? "#fecaca" : "#e2e8f0"}`,
                transition: "all 0.2s",
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <FireExtinguisher sx={{ fontSize: 18, color: formData.es_matafuegos ? "#dc2626" : "#94a3b8" }} />
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>Matafuegos</Typography>
                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>Activar si el equipo es un matafuego</Typography>
                  </Box>
                </Box>
                <Switch name="es_matafuegos" checked={!!formData.es_matafuegos} onChange={handleChange} color="error" />
              </Box>
            </Grid>

            <Grid item xs={12} sx={{ mt: 0.5 }}>
              <SectionHeader icon={<TechIcon sx={{ fontSize: 14 }} />} label="Detalles Técnicos" />
            </Grid>

            {!formData.es_matafuegos && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth size="small" label="Qm3h" name="qm3h" value={formData.qm3h ?? ""} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth size="small" label="Pres. Disparo" name="presdisparo" value={formData.presdisparo ?? ""} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth size="small" label="Potencia" name="potencia" value={formData.potencia ?? ""} onChange={handleChange} />
                </Grid>
              </>
            )}

            {!!formData.es_matafuegos && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth size="small" label="Tipo de Matafuego" name="tipo" value={formData.tipo ?? ""} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth size="small" label="Capacidad (Kgs)" name="capacidad" value={formData.capacidad ?? ""} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth size="small" label="Fecha de Carga" name="fecha_carga" type="date"
                    InputLabelProps={{ shrink: true }} value={formData.fecha_carga ?? ""} onChange={handleChange} />
                </Grid>
              </>
            )}

            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Serie del Equipo" name="serie_equipo"
                value={formData.serie_equipo ?? ""} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Marca" name="marca" value={formData.marca ?? ""} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Modelo" name="modelo" value={formData.modelo ?? ""} onChange={handleChange} />
            </Grid>

            {/* Equipo Crítico */}
            <Grid item xs={12} md={4}>
              <Box sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                px: 2, py: 1.25, borderRadius: 1.5,
                bgcolor: formData.critico === "estado critico" ? "#fffbeb" : "#f8fafc",
                border: `1px solid ${formData.critico === "estado critico" ? "#fde68a" : "#e2e8f0"}`,
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CriticalIcon sx={{ fontSize: 16, color: formData.critico === "estado critico" ? "#d97706" : "#94a3b8" }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>Equipo Crítico</Typography>
                </Box>
                <Switch
                  checked={formData.critico === "estado critico"}
                  onChange={(e) => handleChange({ target: { name: "critico", value: e.target.checked ? "estado critico" : "" } })}
                  color="warning"
                />
              </Box>
            </Grid>

            <Grid item xs={12} sx={{ mt: 0.5 }}>
              <SectionHeader icon={<EngineeringIcon sx={{ fontSize: 14 }} />} label="Descripción y Observaciones" />
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Descripción" name="descripcion" value={formData.descripcion ?? ""}
                onChange={handleChange} multiline rows={3}
                placeholder="Descripción general del equipo y su función" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Observaciones" name="observaciones" value={formData.observaciones ?? ""}
                onChange={handleChange} multiline rows={2} placeholder="Observaciones adicionales" />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: "1px solid #e2e8f0" }}>
          <Button onClick={onClose} color="inherit">Cancelar</Button>
          <Button type="submit" variant="contained" startIcon={<EditIcon />}>
            Guardar Cambios
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
